/**
 * Urgent Liquidity API Service
 * Handles urgent liquidity settings endpoints
 */

import axiosInstance from '@/lib/axios'
import API_ROUTES from '@/lib/api-routes'
import type {
    UrgentLiquiditySettings,
    UrgentLiquiditySettingsResponse,
    UpdateUrgentLiquiditySettingsRequest,
    RepaymentRange,
    CreateRepaymentRangeRequest,
    UpdateRepaymentRangeRequest,
    BulkRepaymentRangesRequest,
} from '@/types/urgent-liquidity'

/**
 * Get urgent liquidity settings
 * GET /admin/urgent-liquidity
 */
export const getUrgentLiquiditySettings =
    async (): Promise<UrgentLiquiditySettings> => {
        const response =
            await axiosInstance.get<UrgentLiquiditySettingsResponse>(
                API_ROUTES.URGENT_LIQUIDITY.SETTINGS
            )
        return response.data.data
    }

/**
 * Update urgent liquidity settings
 * PATCH /admin/urgent-liquidity
 */
export const updateUrgentLiquiditySettings = async (
    data: UpdateUrgentLiquiditySettingsRequest
): Promise<UrgentLiquiditySettings> => {
    const response = await axiosInstance.patch<UrgentLiquiditySettingsResponse>(
        API_ROUTES.URGENT_LIQUIDITY.SETTINGS,
        data
    )
    return response.data.data
}

/**
 * Create a repayment range
 * POST /admin/urgent-liquidity/repayment-ranges
 */
export const createRepaymentRange = async (
    data: CreateRepaymentRangeRequest
): Promise<RepaymentRange> => {
    const response = await axiosInstance.post<{
        status: string
        statusCode: number
        message: string
        data: RepaymentRange
    }>(API_ROUTES.URGENT_LIQUIDITY.REPAYMENT_RANGES, data)
    return response.data.data
}

/**
 * Update a repayment range
 * PATCH /admin/urgent-liquidity/repayment-ranges/{rangeId}
 */
export const updateRepaymentRange = async (
    rangeId: string,
    data: UpdateRepaymentRangeRequest
): Promise<RepaymentRange> => {
    const response = await axiosInstance.patch<{
        status: string
        statusCode: number
        message: string
        data: RepaymentRange
    }>(API_ROUTES.URGENT_LIQUIDITY.UPDATE_REPAYMENT_RANGE(rangeId), data)
    return response.data.data
}

/**
 * Delete a repayment range
 * DELETE /admin/urgent-liquidity/repayment-ranges/{rangeId}
 */
export const deleteRepaymentRange = async (rangeId: string): Promise<void> => {
    await axiosInstance.delete(
        API_ROUTES.URGENT_LIQUIDITY.DELETE_REPAYMENT_RANGE(rangeId)
    )
}

/**
 * Bulk create repayment ranges
 * POST /admin/urgent-liquidity/repayment-ranges/bulk
 */
export const bulkCreateRepaymentRanges = async (
    data: BulkRepaymentRangesRequest
): Promise<RepaymentRange[]> => {
    const response = await axiosInstance.post<{
        status: string
        statusCode: number
        message: string
        data: RepaymentRange[]
    }>(API_ROUTES.URGENT_LIQUIDITY.REPAYMENT_RANGES_BULK, data)
    return response.data.data
}
